import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

const CHART_DATA = {
  2020: [100, 106, 114, 122, 130, 134, 129, 121, 114, 108, 104, 101],
  2021: [100, 109, 120, 132, 143, 148, 139, 130, 121, 113, 107, 102],
  2022: [100, 112, 126, 140, 152, 156, 145, 134, 124, 115, 108, 103],
  2023: [100, 108, 118, 129, 138, 142, 135, 126, 118, 111, 106, 101],
  2024: [100, 110, 122, 134, 142, 146, 138, 129, 120, 113, 107, 102],
};

const LINE_COLORS = ['#86efac', '#4ade80', '#22c55e', '#16a34a', '#15803d'];

export default function Hero() {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);

    const w = rect.width;
    const h = rect.height;
    const pad = { top: 10, right: 10, bottom: 24, left: 32 };
    const chartW = w - pad.left - pad.right;
    const chartH = h - pad.top - pad.bottom;

    ctx.clearRect(0, 0, w, h);

    for (let i = 0; i <= 4; i++) {
      const y = pad.top + (chartH / 4) * i;
      ctx.beginPath();
      ctx.strokeStyle = 'rgba(255,255,255,0.06)';
      ctx.lineWidth = 1;
      ctx.moveTo(pad.left, y);
      ctx.lineTo(pad.left + chartW, y);
      ctx.stroke();
      const val = Math.round(150 - i * 12);
      ctx.fillStyle = 'rgba(255,255,255,0.3)';
      ctx.font = '9px sans-serif';
      ctx.textAlign = 'right';
      ctx.fillText(`${val}%`, pad.left - 4, y + 3);
    }

    const months = ['Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul'];
    for (let i = 0; i < 6; i++) {
      const x = pad.left + (chartW / 5) * i;
      ctx.fillStyle = 'rgba(255,255,255,0.3)';
      ctx.font = '9px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(months[i], x, h - 4);
    }

    const years = Object.keys(CHART_DATA) as unknown as (keyof typeof CHART_DATA)[];
    years.forEach((year, idx) => {
      const data = CHART_DATA[year].slice(1, 7);
      const minVal = 95;
      const maxVal = 165;

      ctx.beginPath();
      data.forEach((val, i) => {
        const x = pad.left + (chartW / (data.length - 1)) * i;
        const y = pad.top + chartH - ((val - minVal) / (maxVal - minVal)) * chartH;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });
      ctx.strokeStyle = LINE_COLORS[idx];
      ctx.lineWidth = idx === 4 ? 2.5 : 1.5;
      ctx.globalAlpha = idx === 4 ? 1 : 0.45;
      ctx.stroke();
      ctx.globalAlpha = 1;
    });
  }, []);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      {/* Full spring background */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/d31bc67f7f031e83253a13a31a21b42d.jpg"
          alt="Spring wheat field"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/30"></div>
      </div>

      {/* Animated particles */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {Array.from({ length: 12 }).map((_, i) => (
          <div
            key={i}
            className="absolute w-1.5 h-1.5 rounded-full bg-yellow-300/40 animate-bounce"
            style={{
              left: `${8 + i * 8}%`,
              top: `${20 + Math.sin(i * 1.2) * 30}%`,
              animationDelay: `${i * 0.3}s`,
              animationDuration: `${2 + (i % 3) * 0.5}s`,
            }}
          ></div>
        ))}
      </div>

      {/* Wheat stalks */}
      <div className="absolute bottom-0 left-0 right-0 h-28 overflow-hidden pointer-events-none z-10">
        <div className="absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-r from-amber-900/70 via-amber-800/60 to-amber-900/70 border-t border-amber-700/40"></div>
        {Array.from({ length: 40 }).map((_, i) => (
          <div
            key={i}
            className="absolute flex flex-col items-center"
            style={{ left: `${(i / 40) * 100}%`, bottom: '22px' }}
          >
            <div
              className="rounded-sm bg-amber-300/80 mb-0.5"
              style={{ width: '5px', height: `${8 + Math.sin(i * 0.9) * 3}px` }}
            ></div>
            <div
              className="bg-gradient-to-t from-amber-700/90 to-yellow-500/70"
              style={{ width: '2px', height: `${45 + Math.sin(i * 0.7) * 18}px`, borderRadius: '1px' }}
            ></div>
          </div>
        ))}
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 pt-24 pb-44 w-full">
        <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
          {/* Left */}
          <div className="flex-1 w-full">
            <div className="inline-flex items-center gap-2 bg-green-500/20 border border-green-400/40 rounded-full px-4 py-1.5 mb-6">
              <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
              <span className="text-green-300 text-xs font-semibold tracking-wide uppercase">{t('hero_badge')}</span>
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl xl:text-6xl font-black text-white leading-tight mb-4">
              {t('hero_title_1')}{' '}
              <span className="text-yellow-400">{t('hero_title_wheat')}</span>{' '}
              {t('hero_title_2')}{' '}
              <span className="text-green-400">{t('hero_title_3')}</span>{' '}
              {t('hero_title_4')}
            </h1>

            {/* Profit badges */}
            <div className="flex flex-wrap gap-3 mb-6 mt-2">
              <div className="flex items-center gap-2 bg-yellow-400/15 border border-yellow-400/30 rounded-xl px-4 py-2">
                <i className="ri-money-euro-circle-line text-yellow-400 text-lg"></i>
                <div>
                  <div className="text-yellow-300 font-black text-lg leading-none">{t('hero_x2_label')}</div>
                  <div className="text-yellow-400/70 text-xs">{t('hero_x2_from')}</div>
                </div>
              </div>
              <div className="flex items-center gap-2 bg-green-400/15 border border-green-400/30 rounded-xl px-4 py-2">
                <i className="ri-money-euro-circle-line text-green-400 text-lg"></i>
                <div>
                  <div className="text-green-300 font-black text-lg leading-none">{t('hero_x3_label')}</div>
                  <div className="text-green-400/70 text-xs">{t('hero_x3_from')}</div>
                </div>
              </div>
              <div className="flex items-center gap-2 bg-emerald-400/15 border border-emerald-400/30 rounded-xl px-4 py-2">
                <i className="ri-money-euro-circle-line text-emerald-400 text-lg"></i>
                <div>
                  <div className="text-emerald-300 font-black text-lg leading-none">{t('hero_x4_label')}</div>
                  <div className="text-emerald-400/70 text-xs">{t('hero_x4_from')}</div>
                </div>
              </div>
            </div>

            <p className="text-white/70 text-sm md:text-base lg:text-lg leading-relaxed mb-8 max-w-xl">
              {t('hero_desc')}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
              <button
                onClick={() => scrollTo('calculator')}
                className="flex items-center justify-center gap-3 bg-green-500 hover:bg-green-400 text-white font-bold px-6 md:px-8 py-3.5 md:py-4 rounded-full transition-all duration-200 hover:-translate-y-0.5 cursor-pointer whitespace-nowrap text-sm md:text-base"
              >
                <span>{t('hero_cta_calc')}</span>
                <span className="w-7 h-7 flex items-center justify-center bg-white/20 rounded-full">
                  <i className="ri-arrow-right-line text-sm"></i>
                </span>
              </button>
              <button
                onClick={() => scrollTo('packages')}
                className="flex items-center justify-center gap-2 border border-white/30 text-white/80 hover:text-white hover:border-white/50 font-semibold px-6 md:px-8 py-3.5 md:py-4 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap text-sm md:text-base"
              >
                <i className="ri-bar-chart-2-line"></i>
                <span>{t('hero_cta_packages')}</span>
              </button>
            </div>
            <div className="flex flex-wrap gap-5 md:gap-8 mt-8 md:mt-10">
              <div>
                <div className="text-yellow-400 font-black text-xl md:text-2xl">{t('hero_stat1_val')}</div>
                <div className="text-white/50 text-xs mt-0.5">{t('hero_stat1_label')}</div>
              </div>
              <div>
                <div className="text-yellow-400 font-black text-xl md:text-2xl">{t('hero_stat2_val')}</div>
                <div className="text-white/50 text-xs mt-0.5">{t('hero_stat2_label')}</div>
              </div>
              <div>
                <div className="text-yellow-400 font-black text-xl md:text-2xl">{t('hero_stat3_val')}</div>
                <div className="text-white/50 text-xs mt-0.5">{t('hero_stat3_label')}</div>
              </div>
            </div>
          </div>

          {/* Right chart */}
          <div className="flex-1 w-full max-w-lg">
            <div className="bg-black/50 backdrop-blur-md border border-white/15 rounded-2xl p-4 md:p-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-white font-semibold text-sm">{t('hero_chart_title')}</div>
                  <div className="text-white/40 text-xs mt-0.5">{t('hero_chart_sub')}</div>
                </div>
                <div className="flex items-center gap-1.5 bg-yellow-500/15 border border-yellow-500/30 rounded-full px-3 py-1">
                  <i className="ri-arrow-up-line text-yellow-400 text-xs"></i>
                  <span className="text-yellow-400 text-xs font-semibold">{t('hero_chart_avg')}</span>
                </div>
              </div>
              <div className="w-full h-40 md:h-44 relative">
                <canvas ref={canvasRef} className="w-full h-full" style={{ width: '100%', height: '100%' }}></canvas>
              </div>
              <div className="flex flex-wrap gap-3 mt-3 pl-2">
                {[2020, 2021, 2022, 2023, 2024].map((year, i) => (
                  <div key={year} className="flex items-center gap-1.5">
                    <span className="w-5 h-0.5 inline-block rounded-full" style={{ backgroundColor: LINE_COLORS[i] }}></span>
                    <span className="text-white/50 text-xs">{year}</span>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div className="bg-white/8 border border-white/10 rounded-xl p-3">
                  <div className="text-yellow-400 font-bold text-lg">{t('hero_chart_max')}</div>
                  <div className="text-white/50 text-xs">{t('hero_chart_max_label')}</div>
                </div>
                <div className="bg-white/8 border border-white/10 rounded-xl p-3">
                  <div className="text-green-400 font-bold text-lg">{t('hero_chart_100')}</div>
                  <div className="text-white/50 text-xs">{t('hero_chart_100_label')}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-32 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 animate-bounce z-20 pointer-events-none">
        <span className="text-white/30 text-xs hidden md:block">{t('hero_scroll')}</span>
        <i className="ri-arrow-down-line text-white/30 text-base"></i>
      </div>
    </section>
  );
}
